/**
 * What a stored file is, decided without touching a platform API: the limits the
 * `documents` bucket enforces (migration 20261022000001), which extension means
 * which MIME type, and whether the app can show the file itself.
 *
 * Platformless because the two ends disagree about everything else: the browser
 * hands over a `File`, the phone hands over bytes and a cache path, and only one
 * of them has a reliable `type`. The numbers here are the ones the storage API
 * will apply anyway — they live in core so the message on screen quotes the same
 * limit the server does.
 */
import { attachmentName } from './text';

/** 50 MB — a scanned lease runs past 20, and the bucket's own limit is this one. */
export const DOCUMENT_MAX_BYTES = 50 * 1024 * 1024;

/**
 * Extension → MIME, and the only source either platform can be trusted on. The
 * browser leaves `File.type` empty for an extension it does not know (`.heic` on
 * older Windows, `.xlsx` on a machine without Office), and the phone's picker is
 * worse: `application/octet-stream` for anything unusual. The bucket rejects both,
 * so the name decides and the declared type is only ever a shortcut.
 *
 * One MIME per extension, even where the bucket accepts two spellings: a zip is
 * uploaded as `application/zip` and an XML as `application/xml`, while the
 * `x-zip-compressed` / `text/xml` the bucket also allows are what *other* senders
 * call them (Windows for the first, half the e-fatura tooling for the second).
 */
const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  heif: 'image/heif',
  webp: 'image/webp',
  gif: 'image/gif',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  csv: 'text/csv',
  rtf: 'application/rtf',
  zip: 'application/zip',
  xml: 'application/xml',
};

/**
 * Everything the `documents` bucket accepts, in the bucket's own order. Keep this
 * equal to `allowed_mime_types` in migration 20261022000001: a type listed here
 * and not there fails at upload with a storage error instead of a Turkish one.
 */
export const DOCUMENT_MIME_TYPES: readonly string[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'image/gif',
  'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/rtf',
  'application/zip',
  'application/x-zip-compressed',
  'application/xml',
  'text/xml',
];

/**
 * The `accept` of a file input. The extensions are repeated after the MIME types
 * on purpose: a picker filters on what the operating system says a file is, and
 * the machines that report `.xlsx` as octet-stream are exactly the ones that would
 * then grey the file out and leave the owner thinking the app cannot take it.
 */
export const DOCUMENT_ACCEPT: string = [...DOCUMENT_MIME_TYPES, ...Object.keys(MIME_BY_EXT).map((ext) => `.${ext}`)].join(',');

/**
 * The extension, lower case and without the dot ("Kira Sözleşmesi.PDF" → "pdf").
 * A leading dot is a hidden file, not an extension, so ".gitignore" has none.
 */
export function fileExtension(name: string): string {
  const base = name.slice(name.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}

/**
 * The MIME type a file should be stored with, or null when neither the name nor
 * the picker can say. A declared type is taken only if the bucket accepts it —
 * that rules out the empty string and `application/octet-stream` in one test and
 * leaves the extension to answer, which is the whole point.
 */
export function mimeForName(name: string, declared?: string | null): string | null {
  // "text/plain; charset=utf-8" is a legitimate thing for a browser to report
  const declaredType = (declared ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
  if (DOCUMENT_MIME_TYPES.includes(declaredType)) return declaredType;
  return MIME_BY_EXT[fileExtension(name)] ?? null;
}

/** The characters a file name may not carry on either platform. */
const UNSAFE_IN_NAME = /[/\\:*?"<>|]+/g;

/**
 * The name a paper leaves the app under — into a download folder, into the
 * accountant's inbox, onto the share sheet.
 *
 * Not the name inside the object key: `safeName` folds that one to ASCII on the
 * way up, and Turkish does not survive the trip. "Vergi Levhası.pdf" is stored as
 * "Vergi-Levhas-.pdf", because dotless ı has no decomposition to strip and simply
 * falls out. `title` is what the row calls the paper — the file's own name unless
 * the owner typed over it — so it is the only honest source, and the extension is
 * put back from the stored path, which is the one thing that still has it for
 * certain. A title that already ends in the right extension is left alone.
 */
export function documentFileName(title: string | null | undefined, storagePath: string): string {
  const stored = attachmentName(storagePath);
  const name = (title ?? '').replace(UNSAFE_IN_NAME, '-').trim();
  if (!name) return stored;
  const ext = fileExtension(stored);
  return !ext || fileExtension(name) === ext ? name : `${name}.${ext}`;
}

/** How a document can be shown in the app; null means hand it to the device. */
export type PreviewKind = 'pdf' | 'image' | 'text' | null;

/**
 * What kind of viewer a file needs. This answers what the file *is*, not what the
 * current renderer manages — a browser cannot decode HEIC or TIFF while iOS shows
 * both, so a screen that renders an image must still fall back to downloading or
 * opening it outside when the decode fails. Office files have no viewer on either
 * platform (we add no dependency for one), so they come back null.
 */
export function previewKind(mime: string | null | undefined): PreviewKind {
  if (!mime) return null;
  const type = mime.split(';')[0]?.trim().toLowerCase() ?? '';
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('image/')) return 'image';
  if (type === 'text/plain' || type === 'text/csv' || type === 'application/xml' || type === 'text/xml') return 'text';
  return null;
}
